/* ==========================================================================
   Add Provider Modal Component
   Task F24: Modal for inviting new providers with email and name
   Updated: Support direct account creation with password
   ========================================================================== */

import { useState, useEffect } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import type { ProviderInvite } from '@/api/types'

/* --------------------------------------------------------------------------
   Props
   -------------------------------------------------------------------------- */

export interface InviteProviderModalProps {
  /** Whether the modal is open */
  open: boolean
  /** Callback when modal should close */
  onClose: () => void
  /** Callback when form is submitted */
  onSubmit: (data: ProviderInvite) => Promise<void>
  /** Whether the form is currently submitting */
  loading?: boolean
  /** Show the optional NPI field. Off for Canadian pharmacies (PORTAL-161). */
  showNpi?: boolean
}

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

type CreationMode = 'invite' | 'direct'

type FieldName = 'email' | 'name' | 'npi' | 'password' | 'confirmPassword'

/* --------------------------------------------------------------------------
   Validation Helpers
   -------------------------------------------------------------------------- */

function validateEmail(email: string): string | null {
  if (!email.trim()) {
    return 'Email is required'
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address'
  }
  return null
}

function validateName(name: string): string | null {
  if (!name.trim()) {
    return 'Name is required'
  }
  if (name.trim().length < 2) {
    return 'Name must be at least 2 characters'
  }
  return null
}

function validateNPI(npi: string): string | null {
  // NPI is optional, but if provided must be 10 digits
  if (!npi.trim()) {
    return null
  }
  if (!/^\d{10}$/.test(npi)) {
    return 'NPI must be exactly 10 digits'
  }
  return null
}

function validatePassword(password: string): string | null {
  if (!password) {
    return 'Password is required'
  }
  if (password.length < 8) {
    return 'Password must be at least 8 characters'
  }
  if (new TextEncoder().encode(password).length > 72) {
    return 'Password must be at most 72 characters (fewer if using non-English characters or emoji)'
  }
  if (!/[A-Z]/.test(password)) {
    return 'Must contain at least one uppercase letter'
  }
  if (!/[a-z]/.test(password)) {
    return 'Must contain at least one lowercase letter'
  }
  if (!/\d/.test(password)) {
    return 'Must contain at least one number'
  }
  return null
}

function validateConfirmPassword(
  password: string,
  confirmPassword: string
): string | null {
  if (!confirmPassword) {
    return 'Please confirm the password'
  }
  if (password !== confirmPassword) {
    return 'Passwords do not match'
  }
  return null
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

/**
 * Modal for adding a new provider.
 * Supports two modes: email invitation or direct account creation with password.
 */
export function InviteProviderModal({
  open,
  onClose,
  onSubmit,
  loading = false,
  showNpi = true,
}: InviteProviderModalProps) {
  // Creation mode
  const [mode, setMode] = useState<CreationMode>('invite')

  // Form state
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [npi, setNpi] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Validation state
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({})

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setMode('invite')
      setEmail('')
      setName('')
      setNpi('')
      setPassword('')
      setConfirmPassword('')
      setShowPassword(false)
      setShowConfirmPassword(false)
      setErrors({})
      setTouched({})
    }
  }, [open])

  // Clear password fields when mode changes
  useEffect(() => {
    setPassword('')
    setConfirmPassword('')
    setShowPassword(false)
    setShowConfirmPassword(false)
    setErrors((prev) => ({
      ...prev,
      password: undefined,
      confirmPassword: undefined,
    }))
    setTouched((prev) => ({
      ...prev,
      password: undefined,
      confirmPassword: undefined,
    }))
  }, [mode])

  // Validate a single field
  function validateField(field: FieldName, value: string): string | null {
    switch (field) {
      case 'email':
        return validateEmail(value)
      case 'name':
        return validateName(value)
      case 'npi':
        return validateNPI(value)
      case 'password':
        return validatePassword(value)
      case 'confirmPassword':
        return validateConfirmPassword(password, value)
      default:
        return null
    }
  }

  // Handle blur - validate and mark as touched
  function handleBlur(field: FieldName) {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const valueMap: Record<FieldName, string> = {
      email,
      name,
      npi,
      password,
      confirmPassword,
    }
    const error = validateField(field, valueMap[field])
    setErrors((prev) => ({ ...prev, [field]: error || undefined }))
  }

  // Handle input change - clear error on change
  function handleChange(field: FieldName, value: string) {
    switch (field) {
      case 'email':
        setEmail(value)
        break
      case 'name':
        setName(value)
        break
      case 'npi':
        // Only allow digits for NPI
        setNpi(value.replace(/\D/g, '').slice(0, 10))
        break
      case 'password':
        setPassword(value)
        // Also re-validate confirm if it's been touched
        if (touched.confirmPassword && confirmPassword) {
          const confirmError = validateConfirmPassword(value, confirmPassword)
          setErrors((prev) => ({
            ...prev,
            confirmPassword: confirmError || undefined,
          }))
        }
        break
      case 'confirmPassword':
        setConfirmPassword(value)
        break
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  // Validate all fields
  function validateForm(): boolean {
    const newErrors: Partial<Record<FieldName, string>> = {}

    const emailError = validateEmail(email)
    if (emailError) newErrors.email = emailError

    const nameError = validateName(name)
    if (nameError) newErrors.name = nameError

    const npiError = validateNPI(npi)
    if (npiError) newErrors.npi = npiError

    if (mode === 'direct') {
      const passwordError = validatePassword(password)
      if (passwordError) newErrors.password = passwordError

      const confirmError = validateConfirmPassword(password, confirmPassword)
      if (confirmError) newErrors.confirmPassword = confirmError
    }

    setErrors(newErrors)
    setTouched({
      email: true,
      name: true,
      npi: true,
      ...(mode === 'direct'
        ? { password: true, confirmPassword: true }
        : {}),
    })

    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const data: ProviderInvite = {
      email: email.trim(),
      name: name.trim(),
      npi: showNpi ? npi.trim() || null : null,
    }

    if (mode === 'direct') {
      data.password = password
    }

    await onSubmit(data)
  }

  const isDirectMode = mode === 'direct'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isDirectMode ? 'Create Provider Account' : 'Invite Provider'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Mode Toggle */}
        <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
          <button
            type="button"
            onClick={() => setMode('invite')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              !isDirectMode
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={loading}
          >
            Email Invitation
          </button>
          <button
            type="button"
            onClick={() => setMode('direct')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              isDirectMode
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={loading}
          >
            Create with Password
          </button>
        </div>

        <p className="text-sm text-gray-600">
          {isDirectMode
            ? 'Create a provider account with a temporary password. Share the credentials with the provider so they can log in immediately.'
            : 'Send an invitation email to a provider. They will receive a link to set up their password and complete their profile.'}
        </p>

        {/* Email */}
        <FormField
          label="Email Address"
          required
          error={touched.email ? errors.email : undefined}
          htmlFor="invite-email"
        >
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            placeholder="provider@example.com"
            disabled={loading}
            autoComplete="off"
          />
        </FormField>

        {/* Name */}
        <FormField
          label="Provider Name"
          required
          error={touched.name ? errors.name : undefined}
          htmlFor="invite-name"
        >
          <Input
            id="invite-name"
            type="text"
            value={name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            placeholder="Dr. Jane Smith"
            disabled={loading}
            autoComplete="off"
          />
        </FormField>

        {/* NPI (Optional) — US pharmacies only */}
        {showNpi && (
        <FormField
          label="NPI (Optional)"
          helperText="10-digit National Provider Identifier. Can be added later by provider."
          error={touched.npi ? errors.npi : undefined}
          htmlFor="invite-npi"
        >
          <Input
            id="invite-npi"
            type="text"
            value={npi}
            onChange={(e) => handleChange('npi', e.target.value)}
            onBlur={() => handleBlur('npi')}
            placeholder="1234567890"
            maxLength={10}
            disabled={loading}
            autoComplete="off"
          />
        </FormField>
        )}

        {/* Password fields (direct mode only) */}
        {isDirectMode && (
          <>
            <FormField
              label="Temporary Password"
              required
              error={touched.password ? errors.password : undefined}
              htmlFor="invite-password"
            >
              <div className="relative">
                <Input
                  id="invite-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  onBlur={() => handleBlur('password')}
                  placeholder="Min 8 characters"
                  disabled={loading}
                  autoComplete="new-password"
                  maxLength={72}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </FormField>

            <FormField
              label="Confirm Password"
              required
              error={
                touched.confirmPassword ? errors.confirmPassword : undefined
              }
              htmlFor="invite-confirm-password"
            >
              <div className="relative">
                <Input
                  id="invite-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) =>
                    handleChange('confirmPassword', e.target.value)
                  }
                  onBlur={() => handleBlur('confirmPassword')}
                  placeholder="Re-enter password"
                  disabled={loading}
                  autoComplete="new-password"
                  maxLength={72}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </FormField>

            <p className="text-xs text-gray-500">
              The provider can change this password after logging in.
            </p>
          </>
        )}

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            className="sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="sm:flex-1"
          >
            {isDirectMode ? 'Create Account' : 'Send Invitation'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default InviteProviderModal
